import React, { useMemo, useRef, useState } from 'react';
import { ERAS, Era, ISSUES, CATEGORIES, Issue } from '../data';
import { gsap, useGSAP, prefersReducedMotion } from '../../../lib/gsap';

const START = new Date('2017-01-01').getTime();
const END = new Date('2026-12-31').getTime();
const YEARS = Array.from({ length: 10 }, (_, i) => 2017 + i);

const toX = (date: string) => {
   const t = new Date(date.length === 7 ? `${date}-15` : date).getTime();
   return ((t - START) / (END - START)) * 100;
};

const formatDate = (date: string) =>
   new Date(date.length === 7 ? `${date}-15` : date).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
      ...(date.length > 7 ? { day: 'numeric' } : {}),
   });

/**
 * One-row timeline: era bands across 2017–2026, one mark per documented issue.
 * Marks stack upward when they fall in the same month so nothing overlaps.
 */
const Timeline = ({ onSelect }: { onSelect?: (id: string) => void }) => {
   const root = useRef<HTMLDivElement>(null);
   const [hover, setHover] = useState<{ issue: Issue; x: number; y: number } | null>(null);

   // Greedy stacking: a mark goes on the lowest row whose previous mark is at least
   // MIN_GAP away horizontally, so hit targets never overlap.
   const { marks, rows } = useMemo(() => {
      const MIN_GAP = 2.2; // percent of width
      const lastX: number[] = [];
      const placed = [...ISSUES]
         .sort((a, b) => a.date.localeCompare(b.date))
         .map(issue => {
            const x = toX(issue.date);
            let level = lastX.findIndex(prev => x - prev >= MIN_GAP);
            if (level === -1) level = lastX.length;
            lastX[level] = x;
            return { issue, x, level };
         });
      return { marks: placed, rows: lastX.length };
   }, []);

   useGSAP(
      () => {
         if (prefersReducedMotion()) return;
         const tl = gsap.timeline({ scrollTrigger: { trigger: root.current, start: 'top 85%', once: true } });
         tl.from('[data-era-band]', { scaleX: 0, transformOrigin: 'left', duration: 1.4, stagger: 0.2, ease: 'expo.inOut' })
            .from('[data-axis-tick]', { autoAlpha: 0, y: 6, stagger: 0.04, duration: 0.6 }, '-=1')
            .from('[data-mark]', { scale: 0, autoAlpha: 0, stagger: 0.025, duration: 0.6, ease: 'back.out(2)' }, '-=0.8');
      },
      { scope: root }
   );

   return (
      <div ref={root} className="relative">
         <div className="relative" style={{ height: Math.max(220, 32 + 6 + rows * 22 + 40) }}>
            {/* Era bands */}
            {(Object.keys(ERAS) as Era[]).map(era => {
               const left = toX(ERAS[era].start);
               const width = toX(ERAS[era].end) - left;
               const isTerm = era !== 'between';
               return (
                  <div
                     key={era}
                     data-era-band
                     className={`absolute bottom-8 top-0 ${
                        isTerm ? 'bg-signal/[0.07] border-x border-signal/20' : 'bg-white/[0.015]'
                     }`}
                     style={{ left: `${left}%`, width: `${width}%` }}
                  >
                     <span
                        className={`absolute left-3 top-3 font-mono text-[10px] uppercase tracking-[0.2em] ${
                           isTerm ? 'text-signal-soft' : 'text-bone-dim'
                        }`}
                     >
                        {ERAS[era].label}
                     </span>
                  </div>
               );
            })}

            {/* Baseline */}
            <div className="absolute inset-x-0 bottom-8 h-px bg-white/15" />

            {/* Marks */}
            {marks.map(({ issue, x, level }) => (
               <button
                  key={issue.id}
                  type="button"
                  data-mark
                  aria-label={`${formatDate(issue.date)}: ${issue.title}`}
                  onMouseEnter={e => {
                     const r = root.current!.getBoundingClientRect();
                     const b = (e.currentTarget as HTMLElement).getBoundingClientRect();
                     setHover({ issue, x: b.left - r.left + b.width / 2, y: b.top - r.top });
                  }}
                  onMouseLeave={() => setHover(null)}
                  onFocus={e => {
                     const r = root.current!.getBoundingClientRect();
                     const b = (e.currentTarget as HTMLElement).getBoundingClientRect();
                     setHover({ issue, x: b.left - r.left + b.width / 2, y: b.top - r.top });
                  }}
                  onBlur={() => setHover(null)}
                  onClick={() => onSelect?.(issue.id)}
                  className="group absolute flex h-5 w-5 -translate-x-1/2 items-center justify-center"
                  style={{ left: `${x}%`, bottom: `${32 + 6 + level * 22}px` }}
               >
                  <span
                     className={`block h-2.5 w-2.5 rounded-full ring-2 ring-ink-950 transition-transform duration-300 group-hover:scale-150 ${
                        hover?.issue.id === issue.id ? 'bg-bone scale-150' : 'bg-signal'
                     }`}
                  />
               </button>
            ))}

            {/* Year axis */}
            <div className="absolute inset-x-0 bottom-0 h-8">
               {YEARS.map(year => (
                  <span
                     key={year}
                     data-axis-tick
                     className="absolute top-2 -translate-x-1/2 font-mono text-[10px] text-bone-dim"
                     style={{ left: `${toX(`${year}-01-01`)}%` }}
                  >
                     <span className="absolute -top-2 left-1/2 h-1.5 w-px bg-white/20" />
                     {`'${String(year).slice(2)}`}
                  </span>
               ))}
            </div>
         </div>

         {hover && (
            <div
               role="tooltip"
               className="pointer-events-none absolute z-20 w-64 -translate-x-1/2 -translate-y-full rounded-sm border border-white/10 bg-ink-900/95 p-3 shadow-2xl backdrop-blur-md"
               style={{
                  left: `clamp(8rem, ${hover.x}px, calc(100% - 8rem))`,
                  top: hover.y - 10,
               }}
            >
               <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-bone-dim">
                  {formatDate(hover.issue.date)} · {CATEGORIES[hover.issue.category]}
               </p>
               <p className="mt-1.5 text-sm leading-snug text-bone">{hover.issue.title}</p>
               {hover.issue.figure && <p className="mt-1.5 font-mono text-xs text-bone-muted">{hover.issue.figure}</p>}
            </div>
         )}
      </div>
   );
};

export default Timeline;
